import React, { useState } from 'react'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/misc/Table";
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "../../components/misc/Pagination";
import Spinner from '../../components/misc/Spinner';
import { ChevronDown, ChevronRight, Trash2, CalendarClock, Play, Pause, Home, Megaphone, FileText, Eye, Tag, Star, Target } from "lucide-react";
import { Button } from '../../components/misc/Button';
import { cn } from '../../utils/classMerger';
import PostPreview from '../../components/post/PostPreview';

import { DashboardUser, UserDetails, CampaignPost } from './types';

interface DashboardTableProps {
  // Data props
  users: DashboardUser[];
  userDetailsCache: {[userId: string]: UserDetails};
  postsCache: {[campaignId: string]: CampaignPost[]};
  promotionPostsCache: {[campaignId: string]: CampaignPost[]};
  postsPagination: {[campaignId: string]: {page: number, limit: number, total: number, totalPages: number}};
  promotionPostsPagination: {[campaignId: string]: {page: number, limit: number, total: number, totalPages: number}};
  myBrandPagination: {[userId: string]: {page: number, limit: number, total: number, totalPages: number}};
  leadGenerationPagination: {[userId: string]: {page: number, limit: number, total: number, totalPages: number}};
  
  // Loading states
  tableLoading: boolean;
  userDetailsLoading: {[userId: string]: boolean};
  postsLoading: {[campaignId: string]: boolean};
  promotionPostsLoading: {[campaignId: string]: boolean};
  myBrandLoading: {[userId: string]: boolean};
  leadGenerationLoading: {[userId: string]: boolean};
  pauseCampaignLoading: {[campaignId: string]: boolean};
  
  // Expanded states
  expandedUserId: string | null;
  setExpandedUserId: (userId: string | null) => void;
  expandedListingId: string | null;
  setExpandedListingId: (listingId: string | null) => void;
  expandedCampaignId: string | null;
  setExpandedCampaignId: (campaignId: string | null) => void;
  expandedPromotionId: string | null;
  setExpandedPromotionId: (promotionId: string | null) => void;
  
  // Handler functions
  fetchUserDetails: (userId: string, sortColumn?: string, sortDirection?: 'asc' | 'desc', page?: number, leadGenPage?: number) => Promise<void>;
  fetchCampaignPosts: (campaignId: string, page?: number, sortColumn?: string, sortDirection?: 'asc' | 'desc') => Promise<void>;
  fetchPromotionPosts: (campaignId: string, page?: number, sortColumn?: string, sortDirection?: 'asc' | 'desc') => Promise<void>;
  handleMyBrandPagination: (userId: string, page: number) => Promise<void>;
  handleLeadGenerationPagination: (userId: string, page: number) => Promise<void>;
  handleTableSort: (column: string, type: 'dashboard' | 'listing' | 'promotion' | 'myBrand' | 'leadGeneration', campaignId?: string) => void;
  renderTableSortIcon: (column: string, type: 'dashboard' | 'listing' | 'promotion' | 'myBrand' | 'leadGeneration', campaignId?: string) => JSX.Element | null;
  handlePauseCampaign: (campaignId: string, newStatus: string, userId?: string, type?: string) => Promise<void>;
  handleSchedulePost: (post: {id: string, platform: string, scheduledFor?: string, campaignType?: 'listing' | 'promotion' | 'myBrand' | 'leadGeneration'}) => void;
  setRemoveToConfirm: (confirm: {type: string, id: string, listingType?: string, userId?: string} | null) => void;
}

const DashboardTable = ({
    handleTableSort,
    tableLoading,
    users: currentUsers,
    setExpandedUserId,
    expandedUserId,
    fetchUserDetails,
    userDetailsLoading,
    userDetailsCache,
    expandedListingId,
    setExpandedListingId,
    expandedCampaignId,
    setExpandedCampaignId,
    fetchCampaignPosts,
    pauseCampaignLoading,
    handlePauseCampaign,
    postsLoading,
    postsCache,
    postsPagination,
    handleSchedulePost,
    renderTableSortIcon,
    setRemoveToConfirm,
    setExpandedPromotionId,
    expandedPromotionId,
    fetchPromotionPosts,
    promotionPostsLoading,
    promotionPostsCache,
    promotionPostsPagination,
    myBrandPagination,
    leadGenerationPagination,
    handleMyBrandPagination,
    handleLeadGenerationPagination,
    myBrandLoading,
    leadGenerationLoading,
}: DashboardTableProps) => {
  const [previewPostId, setPreviewPostId] = useState<string | null>(null);

  const handlePreviewPost = (postId: string) => {
    setPreviewPostId(postId);
  };

  const handleClosePreview = () => {
    setPreviewPostId(null);
  };
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-100 hover:bg-gray-100 cursor-pointer">
            <TableHead
              className="text-left hover:bg-gray-200"
              onClick={() => handleTableSort("nameFirst", "dashboard")}
            >
              User Name {renderTableSortIcon("nameFirst", "dashboard")}
            </TableHead>
            <TableHead
              className="text-left hover:bg-gray-200"
              onClick={() => handleTableSort("email", "dashboard")}
            >
              Email {renderTableSortIcon("email", "dashboard")}
            </TableHead>
            <TableHead
              className="text-left hover:bg-gray-200"
              onClick={() => handleTableSort("status", "dashboard")}
            >
              Status {renderTableSortIcon("status", "dashboard")}
            </TableHead>{" "}
            <TableHead
              className="text-center hover:bg-gray-200"
              onClick={() => handleTableSort("role", "dashboard")}
            >
              Role {renderTableSortIcon("role", "dashboard")}
            </TableHead>{" "}
            <TableHead
              className="text-center hover:bg-gray-200"
              onClick={() => handleTableSort("noOfListing", "dashboard")}
            >
              Listings {renderTableSortIcon("noOfListing", "dashboard")}
            </TableHead>
            <TableHead
              className="text-center hover:bg-gray-200"
              onClick={() => handleTableSort("noOfPromotions", "dashboard")}
            >
              Promotions {renderTableSortIcon("noOfPromotions", "dashboard")}
            </TableHead>
            <TableHead
              className="text-center hover:bg-gray-200"
              onClick={() => handleTableSort("noOfMyBrandPosts", "dashboard")}
            >
              Brand Posts {renderTableSortIcon("noOfMyBrandPosts", "dashboard")}
            </TableHead>
            <TableHead
              className="text-center hover:bg-gray-200"
              onClick={() =>
                handleTableSort("noOfLeadGenerationPosts", "dashboard")
              }
            >
              Lead Posts{" "}
              {renderTableSortIcon("noOfLeadGenerationPosts", "dashboard")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableLoading ? (
            <>
              {" "}
              <TableRow>
                <TableCell className="text-center" colSpan={8}>
                  <Spinner />
                </TableCell>
              </TableRow>{" "}
            </>
          ) : (
            currentUsers.map((user) => (
              <React.Fragment key={user._id}>
                <TableRow>
                  <TableCell className="text-left">
                    <button
                      onClick={() => {
                        setExpandedUserId(
                          expandedUserId === user._id ? null : user._id,
                        );
                        if (expandedUserId !== user._id) {
                          fetchUserDetails(user._id);
                        }
                      }}
                      className="mr-2"
                      aria-label={
                        expandedUserId === user._id
                          ? "Collapse user"
                          : "Expand user"
                      }
                    >
                      {expandedUserId === user._id ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </button>
                    {`${user.nameFirst} ${user.nameLast}`}
                  </TableCell>
                  <TableCell className="text-left">{user.email}</TableCell>
                  <TableCell className="text-left">Active</TableCell>
                  <TableCell className="text-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === "ADMIN"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {user.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    {user.noOfListing || 0}
                  </TableCell>
                  <TableCell className="text-center">
                    {user.noOfPromotions || 0}
                  </TableCell>
                  <TableCell className="text-center">
                    {user.noOfMyBrandPosts || 0}
                  </TableCell>
                  <TableCell className="text-center">
                    {user.noOfLeadGenerationPosts || 0}
                  </TableCell>
                </TableRow>
                {expandedUserId === user._id && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-gray-50 p-0">
                      {userDetailsLoading[user._id] ? (
                        <div className="p-8">
                          <Spinner />
                        </div>
                      ) : (
                        <>
                          {/* Listings Table */}
                          <div className="bg-blue-50 border-l-4 border-blue-400 ml-4 mb-4">
                            <div className="px-6 py-3 bg-blue-100 border-b border-blue-200">
                              <div className="flex items-center gap-2">
                                <Home className="h-5 w-5 text-blue-600" />
                                <strong className="text-blue-800 text-lg">
                                  Listings
                                </strong>
                              </div>
                            </div>
                            <div className="px-4 py-2">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-gray-100 hover:bg-gray-100 cursor-pointer">
                                    <TableHead className="text-left hover:bg-gray-200">
                                      Listing ID
                                    </TableHead>
                                    <TableHead className="text-left hover:bg-gray-200">
                                      Address
                                    </TableHead>
                                    <TableHead className="text-left hover:bg-gray-200">
                                      Listing Type
                                    </TableHead>
                                    <TableHead className="text-center hover:bg-gray-200">
                                      MLS Number
                                    </TableHead>
                                    <TableHead className="text-center hover:bg-gray-200">
                                      Campaigns
                                    </TableHead>
                                    <TableHead className="text-center">
                                      Actions
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {userDetailsCache[user._id]?.listings
                                    ?.length ? (
                                    userDetailsCache[user._id].listings.map(
                                      (listing) => (
                                        <React.Fragment key={listing.id}>
                                          <TableRow>
                                            <TableCell className="text-left">
                                              <button
                                                onClick={() =>
                                                  setExpandedListingId(
                                                    expandedListingId ===
                                                      listing.id
                                                      ? null
                                                      : listing.id,
                                                  )
                                                }
                                                className="mr-2"
                                                aria-label={
                                                  expandedListingId ===
                                                  listing.id
                                                    ? "Collapse listing"
                                                    : "Expand listing"
                                                }
                                              >
                                                {expandedListingId ===
                                                listing.id ? (
                                                  <ChevronDown size={16} />
                                                ) : (
                                                  <ChevronRight size={16} />
                                                )}
                                              </button>
                                              {listing.id}
                                            </TableCell>
                                            <TableCell className="text-left">
                                              {listing.formattedAddress}
                                            </TableCell>

                                            <TableCell className="text-left">
                                              {listing.listingType}
                                            </TableCell>
                                            <TableCell className="text-center">
                                              {listing.listingMlsId || "N/A"}
                                            </TableCell>
                                            <TableCell className="text-center">
                                              {listing.campaign ? 1 : 0}
                                            </TableCell>
                                            <TableCell className="text-center">
                                              <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setRemoveToConfirm({
                                                    type: "listing",
                                                    id: listing.id,
                                                    listingType:
                                                      listing.listingType,
                                                    userId: user._id,
                                                  });
                                                }}
                                              >
                                                <Trash2 className="h-4 w-4 mr-1" />
                                                Remove
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                          {expandedListingId === listing.id && (
                                            <TableRow>
                                              <TableCell
                                                colSpan={6}
                                                className="bg-gray-50 p-0"
                                              >
                                                {/* Campaigns Table */}
                                                <div className="bg-green-50 border-l-4 border-green-400 ml-8 mb-4">
                                                  <div className="px-6 py-3 bg-green-100 border-b border-green-200">
                                                    <div className="flex items-center gap-2">
                                                      <Megaphone className="h-4 w-4 text-green-600" />
                                                      <strong className="text-green-800">
                                                        Campaigns
                                                      </strong>
                                                    </div>
                                                  </div>
                                                  <div className="px-4 py-2">
                                                    <Table className="w-full">
                                                      <TableHeader>
                                                        <TableRow className="bg-gray-100 hover:bg-gray-100 cursor-pointer">
                                                          <TableHead className="text-left">
                                                            Campaign ID
                                                          </TableHead>
                                                          <TableHead className="text-center">
                                                            Posts
                                                          </TableHead>
                                                          <TableHead className="text-center ">
                                                            Status
                                                          </TableHead>
                                                          <TableHead className="text-center w-60">
                                                            Actions
                                                          </TableHead>
                                                        </TableRow>
                                                      </TableHeader>
                                                      <TableBody>
                                                        {listing.campaign ? (
                                                          <React.Fragment
                                                            key={
                                                              listing.campaign
                                                                ._id
                                                            }
                                                          >
                                                            <TableRow>
                                                              <TableCell className="text-left">
                                                                <button
                                                                  onClick={() => {
                                                                    if (
                                                                      !listing.campaign
                                                                    )
                                                                      return;
                                                                    setExpandedCampaignId(
                                                                      expandedCampaignId ===
                                                                        listing
                                                                          .campaign
                                                                          ._id
                                                                        ? null
                                                                        : listing
                                                                            .campaign
                                                                            ._id,
                                                                    );
                                                                    if (
                                                                      expandedCampaignId !==
                                                                      listing
                                                                        .campaign
                                                                        ._id
                                                                    ) {
                                                                      fetchCampaignPosts(
                                                                        listing
                                                                          .campaign
                                                                          ._id,
                                                                      );
                                                                    }
                                                                  }}
                                                                  className="mr-2"
                                                                  aria-label={
                                                                    expandedCampaignId ===
                                                                    listing
                                                                      .campaign
                                                                      ._id
                                                                      ? "Collapse campaign"
                                                                      : "Expand campaign"
                                                                  }
                                                                >
                                                                  {expandedCampaignId ===
                                                                  listing
                                                                    .campaign
                                                                    ._id ? (
                                                                    <ChevronDown
                                                                      size={16}
                                                                    />
                                                                  ) : (
                                                                    <ChevronRight
                                                                      size={16}
                                                                    />
                                                                  )}
                                                                </button>
                                                                {
                                                                  listing
                                                                    .campaign
                                                                    ._id
                                                                }
                                                              </TableCell>
                                                              <TableCell className="text-center">
                                                                {
                                                                  listing
                                                                    .campaign
                                                                    .totalPosts
                                                                }
                                                              </TableCell>
                                                              <TableCell className="text-center">
                                                                {
                                                                  listing
                                                                    .campaign
                                                                    .status
                                                                }
                                                              </TableCell>
                                                              <TableCell className="text-center">
                                                                <div className="flex items-center justify-center space-x-2">
                                                                  {pauseCampaignLoading[
                                                                    listing
                                                                      .campaign
                                                                      ._id
                                                                  ] ? (
                                                                    <Spinner />
                                                                  ) : (
                                                                    <Button
                                                                      variant="outline"
                                                                      size="sm"
                                                                      onClick={(
                                                                        e,
                                                                      ) => {
                                                                        e.stopPropagation();
                                                                        handlePauseCampaign(
                                                                          listing
                                                                            .campaign
                                                                            ?._id as string,
                                                                          listing.campaign?.status.toLowerCase() ===
                                                                            "paused"
                                                                            ? "ACTIVE"
                                                                            : "PAUSED",
                                                                          user._id,
                                                                          "listing",
                                                                        );
                                                                      }}
                                                                    >
                                                                      {listing.campaign.status.toLowerCase() ===
                                                                      "paused" ? (
                                                                        <Play className="h-4 w-4 mr-1" />
                                                                      ) : (
                                                                        <Pause className="h-4 w-4 mr-1" />
                                                                      )}

                                                                      {listing.campaign.status.toLowerCase() ===
                                                                      "paused"
                                                                        ? "Resume"
                                                                        : "Pause"}
                                                                    </Button>
                                                                  )}
                                                                </div>
                                                              </TableCell>{" "}
                                                            </TableRow>
                                                            {expandedCampaignId ===
                                                              listing.campaign
                                                                ._id && (
                                                              <TableRow>
                                                                <TableCell
                                                                  colSpan={6}
                                                                  className="bg-gray-50 p-0"
                                                                >
                                                                  {/* Posts Table */}
                                                                  <div className="bg-purple-50 border-l-4 border-purple-400 ml-12 mb-4">
                                                                    <div className="px-6 py-3 bg-purple-100 border-b border-purple-200">
                                                                      <div className="flex items-center gap-2">
                                                                        <FileText className="h-4 w-4 text-purple-600" />
                                                                        <strong className="text-purple-800">
                                                                          Posts
                                                                        </strong>
                                                                      </div>
                                                                    </div>
                                                                    <div className="px-4 py-2">
                                                                      <Table>
                                                                        <TableHeader>
                                                                          <TableRow className="bg-gray-100 hover:bg-gray-100 cursor-pointer">
                                                                            <TableHead
                                                                              className="text-left hover:bg-gray-200"
                                                                              onClick={() =>
                                                                                handleTableSort(
                                                                                  "_id",
                                                                                  "listing",
                                                                                  listing
                                                                                    .campaign
                                                                                    ?._id,
                                                                                )
                                                                              }
                                                                            >
                                                                              CloudTask
                                                                              ID{" "}
                                                                              {renderTableSortIcon(
                                                                                "_id",
                                                                                "listing",
                                                                                listing
                                                                                  .campaign
                                                                                  ?._id,
                                                                              )}
                                                                            </TableHead>
                                                                            <TableHead
                                                                              className="text-left hover:bg-gray-200"
                                                                              onClick={() =>
                                                                                handleTableSort(
                                                                                  "platform",
                                                                                  "listing",
                                                                                  listing
                                                                                    .campaign
                                                                                    ?._id,
                                                                                )
                                                                              }
                                                                            >
                                                                              Platform{" "}
                                                                              {renderTableSortIcon(
                                                                                "platform",
                                                                                "listing",
                                                                                listing
                                                                                  .campaign
                                                                                  ?._id,
                                                                              )}
                                                                            </TableHead>
                                                                            <TableHead
                                                                              className="text-left hover:bg-gray-200"
                                                                              onClick={() =>
                                                                                handleTableSort(
                                                                                  "postType",
                                                                                  "listing",
                                                                                  listing
                                                                                    .campaign
                                                                                    ?._id,
                                                                                )
                                                                              }
                                                                            >
                                                                              Post
                                                                              Type{" "}
                                                                              {renderTableSortIcon(
                                                                                "postType",
                                                                                "listing",
                                                                                listing
                                                                                  .campaign
                                                                                  ?._id,
                                                                              )}
                                                                            </TableHead>
                                                                            <TableHead
                                                                              className="text-left hover:bg-gray-200"
                                                                              onClick={() =>
                                                                                handleTableSort(
                                                                                  "scheduledFor",
                                                                                  "listing",
                                                                                  listing
                                                                                    .campaign
                                                                                    ?._id,
                                                                                )
                                                                              }
                                                                            >
                                                                              Scheduled{" "}
                                                                              {renderTableSortIcon(
                                                                                "scheduledFor",
                                                                                "listing",
                                                                                listing
                                                                                  .campaign
                                                                                  ?._id,
                                                                              )}
                                                                            </TableHead>
                                                                            <TableHead
                                                                              className="text-left hover:bg-gray-200"
                                                                              onClick={() =>
                                                                                handleTableSort(
                                                                                  "status",
                                                                                  "listing",
                                                                                  listing
                                                                                    .campaign
                                                                                    ?._id,
                                                                                )
                                                                              }
                                                                            >
                                                                              Status{" "}
                                                                              {renderTableSortIcon(
                                                                                "status",
                                                                                "listing",
                                                                                listing
                                                                                  .campaign
                                                                                  ?._id,
                                                                              )}
                                                                            </TableHead>
                                                                            <TableHead className="text-center">
                                                                              Actions
                                                                            </TableHead>
                                                                          </TableRow>
                                                                        </TableHeader>
                                                                        <TableBody>
                                                                          {postsLoading[
                                                                            listing
                                                                              .campaign
                                                                              ._id
                                                                          ] ? (
                                                                            <TableRow>
                                                                              <TableCell
                                                                                colSpan={
                                                                                  7
                                                                                }
                                                                                className="text-center p-8"
                                                                              >
                                                                                <Spinner />
                                                                              </TableCell>
                                                                            </TableRow>
                                                                          ) : postsCache[
                                                                              `${listing.campaign._id}_${postsPagination[listing.campaign._id]?.page || 1}`
                                                                            ]
                                                                              ?.length >
                                                                            0 ? (
                                                                            postsCache[
                                                                              `${listing.campaign._id}_${postsPagination[listing.campaign._id]?.page || 1}`
                                                                            ].map(
                                                                              (
                                                                                post,
                                                                              ) => (
                                                                                <TableRow
                                                                                  key={
                                                                                    post._id
                                                                                  }
                                                                                >
                                                                                  <TableCell className="text-left">
                                                                                    {
                                                                                      post._id
                                                                                    }
                                                                                  </TableCell>
                                                                                  <TableCell className="text-left">
                                                                                    {
                                                                                      post.platform
                                                                                    }
                                                                                  </TableCell>
                                                                                  <TableCell className="text-left">
                                                                                    {
                                                                                      post.type
                                                                                    }
                                                                                  </TableCell>
                                                                                  <TableCell className="text-left">
                                                                                    {new Date(
                                                                                      post.scheduledFor,
                                                                                    ).toLocaleString()}
                                                                                  </TableCell>
                                                                                  <TableCell className="text-left">
                                                                                    {
                                                                                      post.status
                                                                                    }
                                                                                  </TableCell>
                                                                                  <TableCell className="text-center">
                                                                                    <div className="flex items-center justify-center space-x-2">
                                                                                      <Button
                                                                                        variant="outline"
                                                                                        size="sm"
                                                                                        onClick={(
                                                                                          e,
                                                                                        ) => {
                                                                                          e.stopPropagation();
                                                                                          handlePreviewPost(
                                                                                            post._id,
                                                                                          );
                                                                                        }}
                                                                                      >
                                                                                        <Eye className="h-4 w-4 mr-1" />
                                                                                        Preview
                                                                                      </Button>
                                                                                      {
                                                                                        // ![
                                                                                        //   "Completed",
                                                                                        //   "InProgress",
                                                                                        //   "posted",
                                                                                        //   "POSTED",
                                                                                        // ].includes(
                                                                                        //   post.status,
                                                                                        // ) && (
                                                                                        <Button
                                                                                          size="sm"
                                                                                          disabled={[
                                                                                            "Completed",
                                                                                            "InProgress",
                                                                                            "posted",
                                                                                            "POSTED",
                                                                                          ].includes(
                                                                                            post.status,
                                                                                          )}
                                                                                          onClick={() => {
                                                                                            handleSchedulePost(
                                                                                              {
                                                                                                id: post._id,
                                                                                                platform:
                                                                                                  post.platform,
                                                                                                scheduledFor:
                                                                                                  post.scheduledFor,
                                                                                                campaignType:
                                                                                                  "listing",
                                                                                              },
                                                                                            );
                                                                                          }}
                                                                                        >
                                                                                          <CalendarClock className="h-4 w-4 mr-1" />
                                                                                          Schedule
                                                                                        </Button>
                                                                                        // )
                                                                                      }
                                                                                      <Button
                                                                                        variant="destructive"
                                                                                        size="sm"
                                                                                        onClick={() =>
                                                                                          setRemoveToConfirm(
                                                                                            {
                                                                                              type: "post",
                                                                                              id: post._id,
                                                                                              userId:
                                                                                                user._id,
                                                                                            },
                                                                                          )
                                                                                        }
                                                                                      >
                                                                                        <Trash2 className="h-4 w-4 mr-1" />
                                                                                        Remove
                                                                                      </Button>
                                                                                    </div>
                                                                                  </TableCell>
                                                                                </TableRow>
                                                                              ),
                                                                            )
                                                                          ) : (
                                                                            <TableRow>
                                                                              <TableCell
                                                                                colSpan={
                                                                                  7
                                                                                }
                                                                                className="text-gray-500 text-center"
                                                                              >
                                                                                {postsCache[
                                                                                  `${listing.campaign._id}_${postsPagination[listing.campaign._id]?.page || 1}`
                                                                                ] !==
                                                                                undefined
                                                                                  ? "No posts"
                                                                                  : "Loading posts..."}
                                                                              </TableCell>
                                                                            </TableRow>
                                                                          )}
                                                                        </TableBody>
                                                                      </Table>
                                                                      {postsPagination[
                                                                        listing
                                                                          .campaign
                                                                          ._id
                                                                      ]
                                                                        ?.totalPages >
                                                                        1 && (
                                                                        <div className="flex justify-center p-4">
                                                                          <Pagination>
                                                                            <PaginationContent>
                                                                              <PaginationItem>
                                                                                <PaginationPrevious
                                                                                  onClick={() => {
                                                                                    const currentPage =
                                                                                      postsPagination[
                                                                                        listing
                                                                                          .campaign
                                                                                          ?._id as string
                                                                                      ]
                                                                                        .page;
                                                                                    if (
                                                                                      currentPage >
                                                                                      1
                                                                                    ) {
                                                                                      fetchCampaignPosts(
                                                                                        listing
                                                                                          .campaign
                                                                                          ?._id as string,
                                                                                        currentPage -
                                                                                          1,
                                                                                      );
                                                                                    }
                                                                                  }}
                                                                                  className={cn(
                                                                                    postsPagination[
                                                                                      listing
                                                                                        .campaign
                                                                                        ._id
                                                                                    ]
                                                                                      .page ===
                                                                                      1 &&
                                                                                      "pointer-events-none opacity-50",
                                                                                  )}
                                                                                />
                                                                              </PaginationItem>
                                                                              {Array.from(
                                                                                {
                                                                                  length:
                                                                                    Math.min(
                                                                                      5,
                                                                                      postsPagination[
                                                                                        listing
                                                                                          .campaign
                                                                                          ._id
                                                                                      ]
                                                                                        .totalPages,
                                                                                    ),
                                                                                },
                                                                                (
                                                                                  _,
                                                                                  i,
                                                                                ) => {
                                                                                  let pageNum:
                                                                                    | number
                                                                                    | null =
                                                                                    null;
                                                                                  if (
                                                                                    i ===
                                                                                    0
                                                                                  )
                                                                                    pageNum = 1;
                                                                                  else if (
                                                                                    i ===
                                                                                    4
                                                                                  )
                                                                                    pageNum =
                                                                                      postsPagination[
                                                                                        listing
                                                                                          .campaign
                                                                                          ?._id as string
                                                                                      ]
                                                                                        .totalPages;
                                                                                  else if (
                                                                                    postsPagination[
                                                                                      listing
                                                                                        .campaign
                                                                                        ?._id as string
                                                                                    ]
                                                                                      .totalPages <=
                                                                                    5
                                                                                  )
                                                                                    pageNum =
                                                                                      i +
                                                                                      1;
                                                                                  else if (
                                                                                    postsPagination[
                                                                                      listing
                                                                                        .campaign
                                                                                        ?._id as string
                                                                                    ]
                                                                                      .page <=
                                                                                    3
                                                                                  )
                                                                                    pageNum =
                                                                                      i +
                                                                                      1;
                                                                                  else if (
                                                                                    postsPagination[
                                                                                      listing
                                                                                        .campaign
                                                                                        ?._id as string
                                                                                    ]
                                                                                      .page >=
                                                                                    postsPagination[
                                                                                      listing
                                                                                        .campaign
                                                                                        ?._id as string
                                                                                    ]
                                                                                      .totalPages -
                                                                                      2
                                                                                  )
                                                                                    pageNum =
                                                                                      postsPagination[
                                                                                        listing
                                                                                          .campaign
                                                                                          ?._id as string
                                                                                      ]
                                                                                        .totalPages -
                                                                                      4 +
                                                                                      i;
                                                                                  else
                                                                                    pageNum =
                                                                                      postsPagination[
                                                                                        listing
                                                                                          .campaign
                                                                                          ?._id as string
                                                                                      ]
                                                                                        .page -
                                                                                      1 +
                                                                                      i;
                                                                                  if (
                                                                                    !pageNum
                                                                                  ) {
                                                                                    return (
                                                                                      <PaginationItem
                                                                                        key={`e-${i}`}
                                                                                      >
                                                                                        <PaginationEllipsis />
                                                                                      </PaginationItem>
                                                                                    );
                                                                                  }
                                                                                  return (
                                                                                    <PaginationItem
                                                                                      key={
                                                                                        pageNum
                                                                                      }
                                                                                    >
                                                                                      <PaginationLink
                                                                                        isActive={
                                                                                          postsPagination[
                                                                                            listing
                                                                                              .campaign
                                                                                              ?._id as string
                                                                                          ]
                                                                                            .page ===
                                                                                          pageNum
                                                                                        }
                                                                                        onClick={() =>
                                                                                          fetchCampaignPosts(
                                                                                            listing
                                                                                              .campaign
                                                                                              ?._id as string,
                                                                                            pageNum!,
                                                                                          )
                                                                                        }
                                                                                      >
                                                                                        {
                                                                                          pageNum
                                                                                        }
                                                                                      </PaginationLink>
                                                                                    </PaginationItem>
                                                                                  );
                                                                                },
                                                                              )}
                                                                              <PaginationItem>
                                                                                <PaginationNext
                                                                                  onClick={() => {
                                                                                    const currentPage =
                                                                                      postsPagination[
                                                                                        listing
                                                                                          .campaign
                                                                                          ?._id as string
                                                                                      ]
                                                                                        .page;
                                                                                    if (
                                                                                      currentPage <
                                                                                      postsPagination[
                                                                                        listing
                                                                                          .campaign
                                                                                          ?._id as string
                                                                                      ]
                                                                                        .totalPages
                                                                                    ) {
                                                                                      fetchCampaignPosts(
                                                                                        listing
                                                                                          .campaign
                                                                                          ?._id as string,
                                                                                        currentPage +
                                                                                          1,
                                                                                      );
                                                                                    }
                                                                                  }}
                                                                                  className={cn(
                                                                                    postsPagination[
                                                                                      listing
                                                                                        .campaign
                                                                                        ._id
                                                                                    ]
                                                                                      .page ===
                                                                                      postsPagination[
                                                                                        listing
                                                                                          .campaign
                                                                                          ._id
                                                                                      ]
                                                                                        .totalPages &&
                                                                                      "pointer-events-none opacity-50",
                                                                                  )}
                                                                                />
                                                                              </PaginationItem>
                                                                            </PaginationContent>
                                                                          </Pagination>
                                                                        </div>
                                                                      )}
                                                                    </div>
                                                                  </div>
                                                                </TableCell>
                                                              </TableRow>
                                                            )}
                                                          </React.Fragment>
                                                        ) : (
                                                          <TableRow>
                                                            <TableCell
                                                              colSpan={4}
                                                              className="text-gray-500 text-center"
                                                            >
                                                              No campaigns
                                                            </TableCell>
                                                          </TableRow>
                                                        )}
                                                      </TableBody>
                                                    </Table>
                                                  </div>
                                                </div>
                                              </TableCell>
                                            </TableRow>
                                          )}
                                        </React.Fragment>
                                      ),
                                    )
                                  ) : (
                                    <TableRow>
                                      <TableCell
                                        colSpan={6}
                                        className="text-gray-500 text-center"
                                      >
                                        No listings
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                          {/* Promotions Table */}
                          <div className="bg-orange-50 border-l-4 border-orange-400 ml-4 mb-4">
                            <div className="px-6 py-3 bg-orange-100 border-b border-orange-200">
                              <div className="flex items-center gap-2">
                                <Tag className="h-5 w-5 text-orange-600" />
                                <strong className="text-orange-800 text-lg">
                                  Promotions
                                </strong>
                              </div>
                            </div>
                            <div className="px-4 py-2">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-gray-100 hover:bg-gray-100 cursor-pointer">
                                    <TableHead className="text-left hover:bg-gray-200">
                                      Promotion ID
                                    </TableHead>
                                    <TableHead className="text-center hover:bg-gray-200">
                                      Posts
                                    </TableHead>
                                    <TableHead className="text-left hover:bg-gray-200">
                                      Promotion Type
                                    </TableHead>
                                    <TableHead className="text-left hover:bg-gray-200">
                                      Scheduled
                                    </TableHead>
                                    <TableHead className="text-left hover:bg-gray-200">
                                      Status
                                    </TableHead>
                                    <TableHead className="text-center">
                                      Actions
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {userDetailsCache[user._id]?.promotions
                                    ?.length ? (
                                    userDetailsCache[user._id].promotions.map(
                                      (promo) => (
                                        <React.Fragment key={promo.id}>
                                          <TableRow>
                                            <TableCell className="text-left">
                                              {promo.campaign &&
                                              promo.campaign._id ? (
                                                <button
                                                  onClick={() => {
                                                    setExpandedPromotionId(
                                                      expandedPromotionId ===
                                                        promo.id
                                                        ? null
                                                        : promo.id,
                                                    );
                                                    if (
                                                      expandedPromotionId !==
                                                      promo.id
                                                    ) {
                                                      fetchPromotionPosts(
                                                        promo.campaign
                                                          ?._id as string,
                                                      );
                                                    }
                                                  }}
                                                  className="mr-2"
                                                  aria-label={
                                                    expandedPromotionId ===
                                                    promo.id
                                                      ? "Collapse promotion"
                                                      : "Expand promotion"
                                                  }
                                                >
                                                  {expandedPromotionId ===
                                                  promo.id ? (
                                                    <ChevronDown size={16} />
                                                  ) : (
                                                    <ChevronRight size={16} />
                                                  )}
                                                </button>
                                              ) : null}
                                              {promo.id}
                                            </TableCell>
                                            <TableCell className="text-center">
                                              {promo.campaign
                                                ? promo.campaign.totalPosts
                                                : "N/A"}
                                            </TableCell>
                                            <TableCell className="text-left">
                                              {promo.types
                                                ? promo.types.join(", ")
                                                : "N/A"}
                                            </TableCell>
                                            <TableCell className="text-left">
                                              {promo.campaign
                                                ? new Date(
                                                    promo.campaign.startDate,
                                                  ).toLocaleString()
                                                : "N/A"}
                                            </TableCell>
                                            <TableCell className="text-left">
                                              {promo.campaign
                                                ? promo.campaign.status
                                                : "N/A"}
                                            </TableCell>
                                            <TableCell className="text-center min-w-[200px]">
                                              <div className="flex items-center justify-center space-x-2">
                                                {promo.campaign &&
                                                  promo.campaign._id && (
                                                    <>
                                                      {pauseCampaignLoading[
                                                        promo.campaign
                                                          ._id as string
                                                      ] ? (
                                                        <div className="flex items-center justify-center w-[68px] h-8">
                                                          <Spinner />
                                                        </div>
                                                      ) : (
                                                        <Button
                                                          variant="outline"
                                                          size="sm"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePauseCampaign(
                                                              promo.campaign
                                                                ?._id as string,
                                                              promo.campaign?.status.toLowerCase() ===
                                                                "paused"
                                                                ? "ACTIVE"
                                                                : "PAUSED",
                                                              user._id,
                                                              "promo",
                                                            );
                                                          }}
                                                        >
                                                          {promo.campaign?.status.toLowerCase() ===
                                                          "paused" ? (
                                                            <Play className="h-4 w-4 mr-1" />
                                                          ) : (
                                                            <Pause className="h-4 w-4 mr-1" />
                                                          )}
                                                          {promo.campaign?.status.toLowerCase() ===
                                                          "paused"
                                                            ? "Resume"
                                                            : "Pause"}
                                                        </Button>
                                                      )}
                                                    </>
                                                  )}
                                                <Button
                                                  variant="destructive"
                                                  size="sm"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setRemoveToConfirm({
                                                      type: "promo",
                                                      id: promo.id,
                                                      userId: user._id,
                                                    });
                                                  }}
                                                >
                                                  <Trash2 className="h-4 w-4 mr-1" />
                                                  Remove
                                                </Button>
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                          {expandedPromotionId === promo.id &&
                                            promo.campaign &&
                                            promo.campaign._id && (
                                              <TableRow>
                                                <TableCell
                                                  colSpan={6}
                                                  className="bg-gray-50 p-0"
                                                >
                                                  {/* Promotion Posts Table */}
                                                  <div className="bg-purple-50 border-l-4 border-purple-400 ml-8 mb-4">
                                                    <div className="px-6 py-3 bg-purple-100 border-b border-purple-200">
                                                      <div className="flex items-center gap-2">
                                                        <FileText className="h-4 w-4 text-purple-600" />
                                                        <strong className="text-purple-800">
                                                          Posts
                                                        </strong>
                                                      </div>
                                                    </div>
                                                    <div className="px-4 py-2">
                                                      <Table>
                                                        <TableHeader>
                                                          <TableRow className="bg-gray-100 hover:bg-gray-100 cursor-pointer">
                                                            <TableHead
                                                              className="text-left hover:bg-gray-200"
                                                              onClick={() =>
                                                                handleTableSort(
                                                                  "_id",
                                                                  "promotion",
                                                                  promo.campaign
                                                                    ?._id,
                                                                )
                                                              }
                                                            >
                                                              CloudTask ID{" "}
                                                              {renderTableSortIcon(
                                                                "_id",
                                                                "promotion",
                                                                promo.campaign
                                                                  ._id,
                                                              )}
                                                            </TableHead>
                                                            <TableHead
                                                              className="text-left hover:bg-gray-200"
                                                              onClick={() =>
                                                                handleTableSort(
                                                                  "platform",
                                                                  "promotion",
                                                                  promo.campaign
                                                                    ?._id,
                                                                )
                                                              }
                                                            >
                                                              Platform{" "}
                                                              {renderTableSortIcon(
                                                                "platform",
                                                                "promotion",
                                                                promo.campaign
                                                                  ._id,
                                                              )}
                                                            </TableHead>
                                                            <TableHead
                                                              className="text-left hover:bg-gray-200"
                                                              onClick={() =>
                                                                handleTableSort(
                                                                  "type",
                                                                  "promotion",
                                                                  promo.campaign
                                                                    ?._id,
                                                                )
                                                              }
                                                            >
                                                              Type{" "}
                                                              {renderTableSortIcon(
                                                                "type",
                                                                "promotion",
                                                                promo.campaign
                                                                  ._id,
                                                              )}
                                                            </TableHead>
                                                            <TableHead
                                                              className="text-left hover:bg-gray-200"
                                                              onClick={() =>
                                                                handleTableSort(
                                                                  "contentType",
                                                                  "promotion",
                                                                  promo.campaign
                                                                    ?._id,
                                                                )
                                                              }
                                                            >
                                                              Content Type{" "}
                                                              {renderTableSortIcon(
                                                                "contentType",
                                                                "promotion",
                                                                promo.campaign
                                                                  ._id,
                                                              )}
                                                            </TableHead>
                                                            <TableHead
                                                              className="text-left hover:bg-gray-200"
                                                              onClick={() =>
                                                                handleTableSort(
                                                                  "scheduledFor",
                                                                  "promotion",
                                                                  promo.campaign
                                                                    ?._id,
                                                                )
                                                              }
                                                            >
                                                              Scheduled For{" "}
                                                              {renderTableSortIcon(
                                                                "scheduledFor",
                                                                "promotion",
                                                                promo.campaign
                                                                  ._id,
                                                              )}
                                                            </TableHead>
                                                            <TableHead
                                                              className="text-left hover:bg-gray-200"
                                                              onClick={() =>
                                                                handleTableSort(
                                                                  "status",
                                                                  "promotion",
                                                                  promo.campaign
                                                                    ?._id,
                                                                )
                                                              }
                                                            >
                                                              Status{" "}
                                                              {renderTableSortIcon(
                                                                "status",
                                                                "promotion",
                                                                promo.campaign
                                                                  ._id,
                                                              )}
                                                            </TableHead>
                                                            <TableHead className="text-center">
                                                              Actions
                                                            </TableHead>
                                                          </TableRow>
                                                        </TableHeader>
                                                        <TableBody>
                                                          {promotionPostsLoading[
                                                            promo.campaign._id
                                                          ] ? (
                                                            <TableRow>
                                                              <TableCell
                                                                colSpan={6}
                                                                className="text-center p-8"
                                                              >
                                                                <Spinner />
                                                              </TableCell>
                                                            </TableRow>
                                                          ) : promotionPostsCache[
                                                              `${promo.campaign._id}_${promotionPostsPagination[promo.campaign._id]?.page || 1}`
                                                            ]?.length > 0 ? (
                                                            promotionPostsCache[
                                                              `${promo.campaign._id}_${promotionPostsPagination[promo.campaign._id]?.page || 1}`
                                                            ].map((post) => (
                                                              <TableRow
                                                                key={post._id}
                                                              >
                                                                <TableCell className="text-left">
                                                                  {post._id}
                                                                </TableCell>
                                                                <TableCell className="text-left">
                                                                  {
                                                                    post.platform
                                                                  }
                                                                </TableCell>
                                                                <TableCell className="text-left">
                                                                  {post.type}
                                                                </TableCell>
                                                                <TableCell className="text-left">
                                                                  {post?.contentType
                                                                    ? post?.contentType
                                                                    : "N/A"}
                                                                </TableCell>
                                                                <TableCell className="text-left">
                                                                  {new Date(
                                                                    post.scheduledFor,
                                                                  ).toLocaleString()}
                                                                </TableCell>
                                                                <TableCell className="text-left">
                                                                  {post.status}
                                                                </TableCell>
                                                                <TableCell className="text-center">
                                                                  <div className="flex items-center justify-center space-x-2">
                                                                    <Button
                                                                      variant="outline"
                                                                      size="sm"
                                                                      onClick={(
                                                                        e,
                                                                      ) => {
                                                                        e.stopPropagation();
                                                                        handlePreviewPost(
                                                                          post._id,
                                                                        );
                                                                      }}
                                                                    >
                                                                      <Eye className="h-4 w-4 mr-1" />
                                                                      Preview
                                                                    </Button>
                                                                    {
                                                                      // ![
                                                                      //   "posted",
                                                                      //   "POSTED",
                                                                      // ].includes(
                                                                      //   post.status,
                                                                      // ) && (
                                                                      <Button
                                                                        size="sm"
                                                                        disabled={[
                                                                          "posted",
                                                                          "POSTED",
                                                                        ].includes(
                                                                          post.status,
                                                                        )}
                                                                        onClick={(
                                                                          e,
                                                                        ) => {
                                                                          e.stopPropagation();
                                                                          handleSchedulePost(
                                                                            {
                                                                              id: post._id,
                                                                              platform:
                                                                                post.platform,
                                                                              scheduledFor:
                                                                                post.scheduledFor,
                                                                              campaignType:
                                                                                "promotion",
                                                                            },
                                                                          );
                                                                        }}
                                                                      >
                                                                        <CalendarClock className="h-4 w-4 mr-1" />
                                                                        Schedule
                                                                      </Button>
                                                                      // )
                                                                    }
                                                                    <Button
                                                                      variant="destructive"
                                                                      size="sm"
                                                                      onClick={(
                                                                        e,
                                                                      ) => {
                                                                        e.stopPropagation();
                                                                        setRemoveToConfirm(
                                                                          {
                                                                            type: "post",
                                                                            id: post._id,
                                                                            userId:
                                                                              user._id,
                                                                          },
                                                                        );
                                                                      }}
                                                                    >
                                                                      <Trash2 className="h-4 w-4 mr-1" />
                                                                      Remove
                                                                    </Button>
                                                                  </div>
                                                                </TableCell>
                                                              </TableRow>
                                                            ))
                                                          ) : (
                                                            <TableRow>
                                                              <TableCell
                                                                colSpan={6}
                                                                className="text-gray-500 text-center"
                                                              >
                                                                {promotionPostsCache[
                                                                  `${promo.campaign._id}_${promotionPostsPagination[promo.campaign._id]?.page || 1}`
                                                                ] !== undefined
                                                                  ? "No posts"
                                                                  : "Loading posts..."}
                                                              </TableCell>
                                                            </TableRow>
                                                          )}
                                                        </TableBody>
                                                      </Table>
                                                      {promotionPostsPagination[
                                                        promo.campaign._id
                                                      ]?.totalPages > 1 && (
                                                        <div className="flex justify-center p-4">
                                                          <Pagination>
                                                            <PaginationContent>
                                                              <PaginationItem>
                                                                <PaginationPrevious
                                                                  onClick={() => {
                                                                    const currentPage =
                                                                      promotionPostsPagination[
                                                                        promo
                                                                          .campaign
                                                                          ?._id as string
                                                                      ].page;
                                                                    if (
                                                                      currentPage >
                                                                      1
                                                                    ) {
                                                                      fetchPromotionPosts(
                                                                        promo
                                                                          .campaign
                                                                          ?._id as string,
                                                                        currentPage -
                                                                          1,
                                                                      );
                                                                    }
                                                                  }}
                                                                  className={cn(
                                                                    promotionPostsPagination[
                                                                      promo
                                                                        .campaign
                                                                        ._id
                                                                    ].page ===
                                                                      1 &&
                                                                      "pointer-events-none opacity-50",
                                                                  )}
                                                                />
                                                              </PaginationItem>
                                                              {Array.from(
                                                                {
                                                                  length:
                                                                    Math.min(
                                                                      5,
                                                                      promotionPostsPagination[
                                                                        promo
                                                                          .campaign
                                                                          ._id
                                                                      ]
                                                                        .totalPages,
                                                                    ),
                                                                },
                                                                (_, i) => {
                                                                  let pageNum:
                                                                    | number
                                                                    | null =
                                                                    null;
                                                                  if (i === 0)
                                                                    pageNum = 1;
                                                                  else if (
                                                                    i === 4
                                                                  )
                                                                    pageNum =
                                                                      promotionPostsPagination[
                                                                        promo
                                                                          .campaign
                                                                          ?._id as string
                                                                      ]
                                                                        .totalPages;
                                                                  else if (
                                                                    promotionPostsPagination[
                                                                      promo
                                                                        .campaign
                                                                        ?._id as string
                                                                    ]
                                                                      .totalPages <=
                                                                    5
                                                                  )
                                                                    pageNum =
                                                                      i + 1;
                                                                  else if (
                                                                    promotionPostsPagination[
                                                                      promo
                                                                        .campaign
                                                                        ?._id as string
                                                                    ].page <= 3
                                                                  )
                                                                    pageNum =
                                                                      i + 1;
                                                                  else if (
                                                                    promotionPostsPagination[
                                                                      promo
                                                                        .campaign
                                                                        ?._id as string
                                                                    ].page >=
                                                                    promotionPostsPagination[
                                                                      promo
                                                                        .campaign
                                                                        ?._id as string
                                                                    ]
                                                                      .totalPages -
                                                                      2
                                                                  )
                                                                    pageNum =
                                                                      promotionPostsPagination[
                                                                        promo
                                                                          .campaign
                                                                          ?._id as string
                                                                      ]
                                                                        .totalPages -
                                                                      4 +
                                                                      i;
                                                                  else
                                                                    pageNum =
                                                                      promotionPostsPagination[
                                                                        promo
                                                                          .campaign
                                                                          ?._id as string
                                                                      ].page -
                                                                      1 +
                                                                      i;
                                                                  if (
                                                                    !pageNum
                                                                  ) {
                                                                    return (
                                                                      <PaginationItem
                                                                        key={`e-${i}`}
                                                                      >
                                                                        <PaginationEllipsis />
                                                                      </PaginationItem>
                                                                    );
                                                                  }
                                                                  return (
                                                                    <PaginationItem
                                                                      key={
                                                                        pageNum
                                                                      }
                                                                    >
                                                                      <PaginationLink
                                                                        isActive={
                                                                          promotionPostsPagination[
                                                                            promo
                                                                              .campaign
                                                                              ?._id as string
                                                                          ]
                                                                            .page ===
                                                                          pageNum
                                                                        }
                                                                        onClick={() =>
                                                                          fetchPromotionPosts(
                                                                            promo
                                                                              .campaign
                                                                              ?._id as string,
                                                                            pageNum!,
                                                                          )
                                                                        }
                                                                      >
                                                                        {
                                                                          pageNum
                                                                        }
                                                                      </PaginationLink>
                                                                    </PaginationItem>
                                                                  );
                                                                },
                                                              )}
                                                              <PaginationItem>
                                                                <PaginationNext
                                                                  onClick={() => {
                                                                    const currentPage =
                                                                      promotionPostsPagination[
                                                                        promo
                                                                          .campaign
                                                                          ?._id as string
                                                                      ].page;
                                                                    if (
                                                                      currentPage <
                                                                      promotionPostsPagination[
                                                                        promo
                                                                          .campaign
                                                                          ?._id as string
                                                                      ]
                                                                        .totalPages
                                                                    ) {
                                                                      fetchPromotionPosts(
                                                                        promo
                                                                          .campaign
                                                                          ?._id as string,
                                                                        currentPage +
                                                                          1,
                                                                      );
                                                                    }
                                                                  }}
                                                                  className={cn(
                                                                    promotionPostsPagination[
                                                                      promo
                                                                        .campaign
                                                                        ._id
                                                                    ].page ===
                                                                      promotionPostsPagination[
                                                                        promo
                                                                          .campaign
                                                                          ._id
                                                                      ]
                                                                        .totalPages &&
                                                                      "pointer-events-none opacity-50",
                                                                  )}
                                                                />
                                                              </PaginationItem>
                                                            </PaginationContent>
                                                          </Pagination>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                </TableCell>
                                              </TableRow>
                                            )}
                                        </React.Fragment>
                                      ),
                                    )
                                  ) : (
                                    <TableRow>
                                      <TableCell
                                        colSpan={6}
                                        className="text-gray-500 text-center"
                                      >
                                        No promotions
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          </div>

                          {/* My Brand Table */}
                          <div className="bg-yellow-50 border-l-4 border-yellow-400 ml-4 mb-4">
                            <div className="px-6 py-3 bg-yellow-100 border-b border-yellow-200">
                              <div className="flex items-center gap-2">
                                <Star className="h-5 w-5 text-yellow-600" />
                                <strong className="text-yellow-800 text-lg">
                                  My Brand
                                </strong>
                              </div>
                            </div>
                            <div className="px-4 py-2">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-gray-100 hover:bg-gray-100 cursor-pointer">
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "_id",
                                          "myBrand",
                                          user._id,
                                        )
                                      }
                                    >
                                      CloudTask ID{" "}
                                      {renderTableSortIcon(
                                        "_id",
                                        "myBrand",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "platform",
                                          "myBrand",
                                          user._id,
                                        )
                                      }
                                    >
                                      Platform{" "}
                                      {renderTableSortIcon(
                                        "platform",
                                        "myBrand",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "type",
                                          "myBrand",
                                          user._id,
                                        )
                                      }
                                    >
                                      Type{" "}
                                      {renderTableSortIcon(
                                        "type",
                                        "myBrand",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "scheduledFor",
                                          "myBrand",
                                          user._id,
                                        )
                                      }
                                    >
                                      Scheduled For{" "}
                                      {renderTableSortIcon(
                                        "scheduledFor",
                                        "myBrand",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "status",
                                          "myBrand",
                                          user._id,
                                        )
                                      }
                                    >
                                      Status{" "}
                                      {renderTableSortIcon(
                                        "status",
                                        "myBrand",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead className="text-center">
                                      Actions
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {myBrandLoading[user._id] ? (
                                    <TableRow>
                                      <TableCell
                                        colSpan={6}
                                        className="text-center p-8"
                                      >
                                        <Spinner />
                                      </TableCell>
                                    </TableRow>
                                  ) : userDetailsCache[user._id]?.myBrandPosts
                                      ?.length ? (
                                    userDetailsCache[
                                      user._id
                                    ]?.myBrandPosts?.map((brand) => (
                                      <TableRow key={brand._id}>
                                        <TableCell className="text-left">
                                          {brand._id}
                                        </TableCell>
                                        <TableCell className="text-left">
                                          {brand.platform}
                                        </TableCell>
                                        <TableCell className="text-left">
                                          {brand.type}
                                        </TableCell>
                                        <TableCell className="text-left">
                                          {new Date(
                                            brand.scheduledFor,
                                          ).toLocaleString()}
                                        </TableCell>
                                        <TableCell className="text-left">
                                          {brand.status}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          <div className="flex items-center justify-center space-x-2">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePreviewPost(brand._id);
                                              }}
                                            >
                                              <Eye className="h-4 w-4 mr-1" />
                                              Preview
                                            </Button>
                                            <Button
                                              size="sm"
                                              disabled={[
                                                "posted",
                                                "POSTED",
                                              ].includes(brand.status)}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleSchedulePost({
                                                  id: brand._id,
                                                  platform: brand.platform,
                                                  scheduledFor:
                                                    brand.scheduledFor,
                                                  campaignType: "myBrand",
                                                });
                                              }}
                                            >
                                              <CalendarClock className="h-4 w-4 mr-1" />
                                              Schedule
                                            </Button>
                                            <Button
                                              variant="destructive"
                                              size="sm"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setRemoveToConfirm({
                                                  type: "post",
                                                  id: brand._id,
                                                  userId: user._id,
                                                });
                                              }}
                                            >
                                              <Trash2 className="h-4 w-4 mr-1" />
                                              Remove
                                            </Button>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  ) : (
                                    <TableRow>
                                      <TableCell
                                        colSpan={6}
                                        className="text-gray-500 text-center"
                                      >
                                        No brand posts
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>

                              {/* My Brand Pagination */}
                              {myBrandPagination[user._id] &&
                                myBrandPagination[user._id].totalPages > 1 && (
                                  <div className="flex justify-center mt-4">
                                    <Pagination className="w-full">
                                      <PaginationContent>
                                        <PaginationItem>
                                          <PaginationPrevious
                                            onClick={() => {
                                              const currentPage =
                                                myBrandPagination[user._id]
                                                  ?.page || 1;
                                              if (currentPage > 1) {
                                                handleMyBrandPagination(
                                                  user._id,
                                                  currentPage - 1,
                                                );
                                              }
                                            }}
                                            className={cn(
                                              myBrandPagination[user._id]
                                                ?.page === 1 &&
                                                "pointer-events-none opacity-50",
                                            )}
                                          />
                                        </PaginationItem>
                                        {Array.from(
                                          {
                                            length: Math.min(
                                              5,
                                              myBrandPagination[user._id]
                                                .totalPages,
                                            ),
                                          },
                                          (_, i) => {
                                            const currentPage =
                                              myBrandPagination[user._id]
                                                ?.page || 1;
                                            const totalPages =
                                              myBrandPagination[user._id]
                                                ?.totalPages || 1;
                                            let pageNum: number | null = null;
                                            if (i === 0) pageNum = 1;
                                            else if (i === 4)
                                              pageNum = totalPages;
                                            else if (totalPages <= 5)
                                              pageNum = i + 1;
                                            else if (currentPage <= 3)
                                              pageNum = i + 1;
                                            else if (
                                              currentPage >=
                                              totalPages - 2
                                            )
                                              pageNum = totalPages - 4 + i;
                                            else pageNum = currentPage - 1 + i;
                                            if (!pageNum) {
                                              return (
                                                <PaginationItem key={`e-${i}`}>
                                                  <PaginationEllipsis />
                                                </PaginationItem>
                                              );
                                            }
                                            return (
                                              <PaginationItem key={pageNum}>
                                                <PaginationLink
                                                  isActive={
                                                    currentPage === pageNum
                                                  }
                                                  onClick={() =>
                                                    handleMyBrandPagination(
                                                      user._id,
                                                      pageNum!,
                                                    )
                                                  }
                                                >
                                                  {pageNum}
                                                </PaginationLink>
                                              </PaginationItem>
                                            );
                                          },
                                        )}
                                        <PaginationItem>
                                          <PaginationNext
                                            onClick={() => {
                                              const currentPage =
                                                myBrandPagination[user._id]
                                                  ?.page || 1;
                                              const totalPages =
                                                myBrandPagination[user._id]
                                                  ?.totalPages || 1;
                                              if (currentPage < totalPages) {
                                                handleMyBrandPagination(
                                                  user._id,
                                                  currentPage + 1,
                                                );
                                              }
                                            }}
                                            className={cn(
                                              myBrandPagination[user._id]
                                                ?.page ===
                                                myBrandPagination[user._id]
                                                  ?.totalPages &&
                                                "pointer-events-none opacity-50",
                                            )}
                                          />
                                        </PaginationItem>
                                      </PaginationContent>
                                    </Pagination>
                                  </div>
                                )}
                            </div>
                          </div>

                          {/* Lead Generation Table */}
                          <div className="bg-purple-50 border-l-4 border-purple-400 ml-4 mb-4">
                            <div className="px-6 py-3 bg-purple-100 border-b border-purple-200">
                              <div className="flex items-center gap-2">
                                <Target className="h-5 w-5 text-purple-600" />
                                <strong className="text-purple-800 text-lg">
                                  Lead Generation
                                </strong>
                              </div>
                            </div>
                            <div className="px-4 py-2">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-gray-100 hover:bg-gray-100 cursor-pointer">
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "_id",
                                          "leadGeneration",
                                          user._id,
                                        )
                                      }
                                    >
                                      CloudTask ID{" "}
                                      {renderTableSortIcon(
                                        "_id",
                                        "leadGeneration",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "platform",
                                          "leadGeneration",
                                          user._id,
                                        )
                                      }
                                    >
                                      Platform{" "}
                                      {renderTableSortIcon(
                                        "platform",
                                        "leadGeneration",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "type",
                                          "leadGeneration",
                                          user._id,
                                        )
                                      }
                                    >
                                      Type{" "}
                                      {renderTableSortIcon(
                                        "type",
                                        "leadGeneration",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "scheduledFor",
                                          "leadGeneration",
                                          user._id,
                                        )
                                      }
                                    >
                                      Scheduled For{" "}
                                      {renderTableSortIcon(
                                        "scheduledFor",
                                        "leadGeneration",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead
                                      className="text-left hover:bg-gray-200"
                                      onClick={() =>
                                        handleTableSort(
                                          "status",
                                          "leadGeneration",
                                          user._id,
                                        )
                                      }
                                    >
                                      Status{" "}
                                      {renderTableSortIcon(
                                        "status",
                                        "leadGeneration",
                                        user._id,
                                      )}
                                    </TableHead>
                                    <TableHead className="text-center">
                                      Actions
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {leadGenerationLoading[user._id] ? (
                                    <TableRow>
                                      <TableCell
                                        colSpan={6}
                                        className="text-center p-8"
                                      >
                                        <Spinner />
                                      </TableCell>
                                    </TableRow>
                                  ) : (userDetailsCache[user._id] as any)
                                      ?.leadGenerationPosts?.length ? (
                                    (
                                      userDetailsCache[user._id] as any
                                    )?.leadGenerationPosts?.map(
                                      (leadGen: any) => (
                                        <TableRow key={leadGen._id}>
                                          <TableCell className="text-left">
                                            {leadGen._id}
                                          </TableCell>
                                          <TableCell className="text-left">
                                            {leadGen.platform}
                                          </TableCell>
                                          <TableCell className="text-left">
                                            {leadGen.type}
                                          </TableCell>
                                          <TableCell className="text-left">
                                            {new Date(
                                              leadGen.scheduledFor,
                                            ).toLocaleString()}
                                          </TableCell>
                                          <TableCell className="text-left">
                                            {leadGen.status}
                                          </TableCell>
                                          <TableCell className="text-center">
                                            <div className="flex items-center justify-center space-x-2">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handlePreviewPost(
                                                    leadGen._id,
                                                  );
                                                }}
                                              >
                                                <Eye className="h-4 w-4 mr-1" />
                                                Preview
                                              </Button>
                                              <Button
                                                size="sm"
                                                disabled={[
                                                  "posted",
                                                  "POSTED",
                                                ].includes(leadGen.status)}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleSchedulePost({
                                                    id: leadGen._id,
                                                    platform: leadGen.platform,
                                                    scheduledFor:
                                                      leadGen.scheduledFor,
                                                    campaignType:
                                                      "leadGeneration",
                                                  });
                                                }}
                                              >
                                                <CalendarClock className="h-4 w-4 mr-1" />
                                                Schedule
                                              </Button>
                                              <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setRemoveToConfirm({
                                                    type: "post",
                                                    id: leadGen._id,
                                                    userId: user._id,
                                                  });
                                                }}
                                              >
                                                <Trash2 className="h-4 w-4 mr-1" />
                                                Remove
                                              </Button>
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      ),
                                    )
                                  ) : (
                                    <TableRow>
                                      <TableCell
                                        colSpan={6}
                                        className="text-gray-500 text-center"
                                      >
                                        No lead generation posts
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>

                              {/* Lead Generation Pagination */}
                              {leadGenerationPagination[user._id] &&
                                leadGenerationPagination[user._id].totalPages >
                                  1 && (
                                  <div className="flex justify-center mt-4">
                                    <Pagination className="w-full">
                                      <PaginationContent>
                                        <PaginationItem>
                                          <PaginationPrevious
                                            onClick={() => {
                                              const currentPage =
                                                leadGenerationPagination[
                                                  user._id
                                                ]?.page || 1;
                                              if (currentPage > 1) {
                                                handleLeadGenerationPagination(
                                                  user._id,
                                                  currentPage - 1,
                                                );
                                              }
                                            }}
                                            className={cn(
                                              leadGenerationPagination[user._id]
                                                ?.page === 1 &&
                                                "pointer-events-none opacity-50",
                                            )}
                                          />
                                        </PaginationItem>
                                        {Array.from(
                                          {
                                            length: Math.min(
                                              5,
                                              leadGenerationPagination[user._id]
                                                .totalPages,
                                            ),
                                          },
                                          (_, i) => {
                                            const currentPage =
                                              leadGenerationPagination[user._id]
                                                ?.page || 1;
                                            const totalPages =
                                              leadGenerationPagination[user._id]
                                                ?.totalPages || 1;
                                            let pageNum: number | null = null;
                                            if (i === 0) pageNum = 1;
                                            else if (i === 4)
                                              pageNum = totalPages;
                                            else if (totalPages <= 5)
                                              pageNum = i + 1;
                                            else if (currentPage <= 3)
                                              pageNum = i + 1;
                                            else if (
                                              currentPage >=
                                              totalPages - 2
                                            )
                                              pageNum = totalPages - 4 + i;
                                            else pageNum = currentPage - 1 + i;
                                            if (!pageNum) {
                                              return (
                                                <PaginationItem key={`e-${i}`}>
                                                  <PaginationEllipsis />
                                                </PaginationItem>
                                              );
                                            }
                                            return (
                                              <PaginationItem key={pageNum}>
                                                <PaginationLink
                                                  isActive={
                                                    currentPage === pageNum
                                                  }
                                                  onClick={() =>
                                                    handleLeadGenerationPagination(
                                                      user._id,
                                                      pageNum!,
                                                    )
                                                  }
                                                >
                                                  {pageNum}
                                                </PaginationLink>
                                              </PaginationItem>
                                            );
                                          },
                                        )}
                                        <PaginationItem>
                                          <PaginationNext
                                            onClick={() => {
                                              const currentPage =
                                                leadGenerationPagination[
                                                  user._id
                                                ]?.page || 1;
                                              const totalPages =
                                                leadGenerationPagination[
                                                  user._id
                                                ]?.totalPages || 1;
                                              if (currentPage < totalPages) {
                                                handleLeadGenerationPagination(
                                                  user._id,
                                                  currentPage + 1,
                                                );
                                              }
                                            }}
                                            className={cn(
                                              leadGenerationPagination[user._id]
                                                ?.page ===
                                                leadGenerationPagination[
                                                  user._id
                                                ]?.totalPages &&
                                                "pointer-events-none opacity-50",
                                            )}
                                          />
                                        </PaginationItem>
                                      </PaginationContent>
                                    </Pagination>
                                  </div>
                                )}
                            </div>
                          </div>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>

      {/* Post Preview Dialog */}
      <PostPreview
        isOpen={!!previewPostId}
        onClose={handleClosePreview}
        postId={previewPostId || ""}
      />
    </>
  );
}

export default DashboardTable
